import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useHeaderContext } from "../../../context/headerContext";
import { useUserContext } from "../../../context/userContext";
import PersonCard from "../../../components/PersonCard";
import { differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays, parseISO } from "date-fns";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

export default function IncidentHistory() {
    const { setTitle, setBackButton } = useHeaderContext();
    const { user, logout, updateTokens } = useUserContext();
    const [ incidentHistory, setIncidentHistory ] = useState([]);  
    const [ lastUpdatedText, setLastUpdatedText ] = useState('');
    const { participantId } = useParams();

    const fetchIncidentHistory = async () => {
      await Sentry.startSpan({ name: "Fetch Incident History", op: "http.request" }, async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Incident/FetchHistoricalIncidentsForParticipant?ParticipantId=${participantId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );
    
          span.setAttribute("http.status_code", response.status);
    
          if (response.ok) {
            const data = await response.json();
            LogErrors(user, logout, updateTokens, data);
            setIncidentHistory(Array.isArray(data) ? data : [data]);
          } else {
            LogErrors(user, logout, updateTokens, "Error fetching incident history");
            throw new Error("Failed to fetch incident history");
          }
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error fetching incident history: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };    

    useEffect(() => {
        setTitle("INCIDENT HISTORY");
        setBackButton(true);
        fetchIncidentHistory();
    }, [])
  

  const groupedIncidents = incidentHistory.reduce((acc, curr) => {
    const date = new Date(curr.WhenOpened).toLocaleDateString('en-GB');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(curr);
    return acc;
  }, {});

  const updateLastUpdatedText = () => {
    if (incidentHistory.length === 0) return;

    const mostRecentIncident = incidentHistory.reduce((latest, incident) => {
      const incidentDate = parseISO(incident.LastUpdated);
      return incidentDate > latest ? incidentDate : latest;
    }, new Date(0));

    const now = new Date();
    const diffInSeconds = differenceInSeconds(now, mostRecentIncident);
    let timeText = '';

    if (diffInSeconds < 60) {
      timeText = `${diffInSeconds} SECS AGO`;
    } else if (diffInSeconds < 3600) {
      const diffInMinutes = differenceInMinutes(now, mostRecentIncident);
      timeText = `${diffInMinutes} MINS AGO`;
    } else if (diffInSeconds < 86400) {
      const diffInHours = differenceInHours(now, mostRecentIncident);
      timeText = `${diffInHours} HOURS AGO`;
    } else {
      const diffInDays = differenceInDays(now, mostRecentIncident);
      timeText = `${diffInDays} DAYS AGO`;
    }

    setLastUpdatedText(timeText);
  };

  useEffect(() => {
    updateLastUpdatedText();
    const intervalId = setInterval(updateLastUpdatedText, 60000);
    return () => clearInterval(intervalId);
  }, [incidentHistory]);

  return (
    <div className="bg-[#F8F8F8] pt-10 pb-[105px] px-6 flex flex-col h-full">
      <div className="mb-10">
        <p className="text-[12px] font-semibold leading-6 spacing tracking-[0.2em] mb-1">
          LAST UPDATED • <span className="text-[12px] font-semibold leading-6 spacing tracking-[0.2em]">{lastUpdatedText}</span>
        </p>
        <h2>Incident History</h2>
      </div>
      <div>
        {Object.keys(groupedIncidents).map(date => (
          <div key={date}>
            <p className="text-[12px] font-semibold leading-6 spacing tracking-[0.2em] mb-3 mt-3">
              {date}
            </p>
            <div className="flex flex-col gap-y-3">
                {groupedIncidents[date].map(incident => {
                  return (
                    <PersonCard
                        key={incident?.EmergencyId}
                        userData={incident}
                        type="emergency"
                        href={`/participant/${participantId}/incident-history/${incident.EmergencyId}`}
                        venueName={incident?.OrgPartnerFriendlyName}
                    />
                  );
                })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}